import { Link } from 'react-router-dom';
import Logo from '../../components/feature/Logo';

export default function Datenschutz() {
  return (
    <div className="min-h-screen bg-white">
      {/* Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": `${import.meta.env.VITE_SITE_URL || 'https://learnsnap.online'}/datenschutz`,
            "url": `${import.meta.env.VITE_SITE_URL || 'https://learnsnap.online'}/datenschutz`,
            "name": "Datenschutzerklärung – LearnSnap | DSGVO-konforme KI-Lernplattform",
            "description": "Datenschutzerklärung von LearnSnap. Transparente Informationen zur Verarbeitung personenbezogener Daten, KI-Nutzung und Ihren Rechten gemäß DSGVO.",
            "inLanguage": "de",
            "isPartOf": { "@id": `${import.meta.env.VITE_SITE_URL || 'https://learnsnap.online'}/#website` },
            "about": {
              "@type": "Thing",
              "name": "Datenschutz & DSGVO"
            }
          })
        }}
      />
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <Logo size="md" showText={true} textColor="text-slate-900" />
            </Link>
            <Link to="/" className="text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line mr-2"></i>Zurück zur Startseite
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Datenschutzerklärung</h1>
        
        <div className="prose prose-slate max-w-none">
          <p className="text-gray-600 mb-8">Stand: Januar 2025</p>

          {/* Important Privacy Notice */}
          <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-6 mb-8 not-prose">
            <div className="flex items-start gap-3">
              <i className="ri-shield-check-line text-blue-600 text-2xl flex-shrink-0"></i>
              <div>
                <h3 className="text-lg font-bold text-blue-900 mb-2">Deine Daten sind sicher</h3>
                <p className="text-sm text-blue-800 leading-relaxed">
                  Wir nehmen den Schutz deiner persönlichen Daten sehr ernst. Diese Datenschutzerklärung 
                  informiert dich transparent darüber, wie wir deine Daten verarbeiten, speichern und schützen - 
                  insbesondere im Zusammenhang mit unserer KI-Technologie.
                </p>
              </div>
            </div>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Verantwortliche Stelle</h2>
            <div className="bg-slate-50 rounded-lg p-6">
              <p className="text-gray-700 mb-2"><strong>Future Now UG</strong></p>
              <p className="text-gray-700 mb-2">Max-Planck-Str. 4</p>
              <p className="text-gray-700 mb-2">85609 Aschheim</p>
              <p className="text-gray-700 mb-4">
                E-Mail:{' '}
                <a href="mailto:info@future-now.online" className="text-teal-600 hover:underline">
                  info@future-now.online
                </a>
              </p>
              <p className="text-gray-700">
                Vertreten durch: [Geschäftsführer Name]
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Allgemeine Hinweise</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen 
              Daten passiert, wenn Sie unsere Website und Plattform nutzen. Personenbezogene Daten sind alle 
              Daten, mit denen Sie persönlich identifiziert werden können.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Datenerfassung auf unserer Website</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.1 Registrierung und Account-Daten</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Bei der Registrierung erfassen wir folgende Daten:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Name (Vor- und Nachname)</li>
              <li>E-Mail-Adresse</li>
              <li>Passwort (verschlüsselt gespeichert)</li>
              <li>Klassenstufe und Schulfächer (optional)</li>
              <li>Registrierungsdatum</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.2 Hochgeladene Lernmaterialien</h3>
            <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-4 mb-4">
              <p className="text-sm text-amber-900 leading-relaxed">
                <strong>Wichtig:</strong> Wenn du Screenshots oder Dokumente hochlädst, werden diese von unserer 
                KI analysiert. Die Inhalte werden verschlüsselt gespeichert und ausschließlich zur Erstellung 
                personalisierter Lernmaterialien verwendet.
              </p>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              Wir verarbeiten:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Hochgeladene Bilder und PDF-Dateien</li>
              <li>Extrahierte Textinhalte aus den Uploads</li>
              <li>Metadaten (Dateiname, Upload-Zeitpunkt, Dateigröße)</li>
              <li>Zuordnung zu Fächern und Kapiteln</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung) und 
              Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Speicherdauer:</strong> Bis zur Löschung durch den Nutzer oder Kündigung des Accounts
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.3 DSGVO-konforme Speicherung hochgeladener Inhalte</h3>
            <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-6 mb-4">
              <p className="text-sm text-blue-900 leading-relaxed mb-4">
                <strong>Datenschutzgarantie:</strong> Hochgeladene Inhalte wie Screenshots, Fotos oder Textauszüge werden ausschließlich zur individuellen Lernunterstützung verarbeitet. Die Daten werden rechtskonform gemäß DSGVO gespeichert und nur in den dafür vorgesehenen Systemen (z. B. Readdy Collections, Supabase Storage oder vergleichbaren sicheren Datenbanken) abgelegt.
              </p>
              <p className="text-sm text-blue-900 leading-relaxed mb-4">
                Die Verarbeitung der Inhalte erfolgt über geprüfte KI-Schnittstellen (APIs). Bei der Nutzung von KI-Diensten über API wird sichergestellt, dass sämtliche Inhalte ausschließlich zur Erstellung der angeforderten Antworten genutzt werden. <strong>Der eingesetzte KI-Dienst verwendet diese Inhalte nicht zur Weiterentwicklung oder zum Training der Modelle.</strong> Eine Weitergabe der Inhalte an Dritte sowie eine Nutzung außerhalb des Lernzwecks findet nicht statt.
              </p>
              <p className="text-sm text-blue-900 leading-relaxed mb-4">
                Personenbezogene Daten, die versehentlich in Screenshots enthalten sind, werden vor der Verarbeitung anonymisiert oder automatisch entfernt. Die Inhalte werden nur so lange gespeichert, wie sie für den Lernfortschritt des Nutzers erforderlich sind. Nutzer können ihre Daten jederzeit löschen.
              </p>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.4 Lernaktivitäten und Fortschrittsdaten</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Zur Personalisierung deiner Lernerfahrung speichern wir:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Beantwortete Fragen und Antworten</li>
              <li>Richtige und falsche Antworten</li>
              <li>Lernzeiten und Sitzungsdauer</li>
              <li>Fortschrittsstatistiken und Erfolgsquoten</li>
              <li>Wiederholungsintervalle (Spaced Repetition)</li>
              <li>Schwachstellen und Stärken-Profile</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Zweck:</strong> Diese Daten ermöglichen es unserer KI, deine Lernerfahrung zu personalisieren 
              und dir genau die Übungen zu zeigen, die du brauchst.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. KI-Verarbeitung und Datennutzung</h2>
            
            <div className="bg-indigo-50 border-l-4 border-indigo-500 rounded-lg p-6 mb-6 not-prose">
              <div className="flex items-start gap-3">
                <i className="ri-brain-line text-indigo-600 text-2xl flex-shrink-0"></i>
                <div>
                  <h3 className="text-lg font-bold text-indigo-900 mb-2">Transparenz bei KI-Nutzung</h3>
                  <p className="text-sm text-indigo-800 leading-relaxed">
                    Unsere KI-Technologie ist das Herzstück von Learnsnap. Hier erfährst du genau, 
                    wie deine Daten zur KI-Personalisierung verwendet werden.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">4.1 KI-Analyse hochgeladener Materialien</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Wenn du Lernmaterialien hochlädst, werden diese von unserer KI analysiert, um:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Themen und Konzepte zu erkennen</li>
              <li>Passende Übungsfragen zu generieren</li>
              <li>Schwierigkeitsgrade einzuschätzen</li>
              <li>Zusammenhänge zwischen Themen zu identifizieren</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              Die KI-Verarbeitung erfolgt auf sicheren Servern innerhalb der EU. Deine Uploads werden 
              nicht mit anderen Nutzern geteilt.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">4.2 Personalisierung durch KI</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Die KI nutzt deine Lernaktivitäten, um:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Dein individuelles Stärken-Schwächen-Profil zu erstellen</li>
              <li>Schwierigkeitsgrade automatisch anzupassen</li>
              <li>Optimale Wiederholungszeitpunkte zu berechnen</li>
              <li>Personalisierte Lernpfade zu empfehlen</li>
              <li>Erklärungen an dein Niveau anzupassen</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">4.3 Keine Nutzung zur KI-Modellentwicklung</h3>
            <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4 mb-4">
              <p className="text-sm text-green-900 leading-relaxed">
                <strong>Wichtige Garantie:</strong> Deine hochgeladenen Inhalte werden NICHT zur Weiterentwicklung oder zum Training von KI-Modellen verwendet. Die Verarbeitung erfolgt ausschließlich über sichere API-Schnittstellen, die deine Daten nur zur unmittelbaren Beantwortung deiner Anfragen nutzen. Eine Verwendung für Trainingszwecke ist technisch ausgeschlossen.
              </p>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">4.4 Externe KI-Dienste</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Wir nutzen externe KI-Dienste (z.B. OpenAI) zur Verarbeitung von Lerninhalten über sichere API-Schnittstellen. 
              Diese Dienste verarbeiten Daten gemäß ihren eigenen Datenschutzrichtlinien. Wir haben mit 
              allen Anbietern Auftragsverarbeitungsverträge (AVV) gemäß Art. 28 DSGVO abgeschlossen.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>API-Nutzung:</strong> Die Verarbeitung erfolgt ausschließlich über API-Schnittstellen, bei denen sichergestellt ist, dass die übermittelten Inhalte nicht für das Training oder die Weiterentwicklung der KI-Modelle verwendet werden.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Cookies und Tracking</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Unsere Website verwendet Cookies, um die Nutzererfahrung zu verbessern. Cookies sind kleine 
              Textdateien, die auf Ihrem Gerät gespeichert werden.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">5.1 Notwendige Cookies</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Diese Cookies sind für den Betrieb der Website erforderlich:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Session-Cookies für die Anmeldung</li>
              <li>Sicherheits-Cookies zum Schutz vor Angriffen</li>
              <li>Funktionale Cookies für Spracheinstellungen</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">5.2 Analyse-Cookies</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Mit Ihrer Einwilligung verwenden wir Analyse-Tools, um die Nutzung unserer Website zu verstehen 
              und zu verbessern. Sie können diese Cookies in den Einstellungen deaktivieren.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Datensicherheit</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Wir setzen technische und organisatorische Sicherheitsmaßnahmen ein, um Ihre Daten zu schützen:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>SSL/TLS-Verschlüsselung für alle Datenübertragungen</li>
              <li>Verschlüsselte Speicherung sensibler Daten</li>
              <li>Regelmäßige Sicherheits-Updates und Backups</li>
              <li>Zugriffsbeschränkungen und Authentifizierung</li>
              <li>Regelmäßige Sicherheitsaudits</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Ihre Rechte</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Sie haben folgende Rechte bezüglich Ihrer personenbezogenen Daten:
            </p>
            
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">
                  <i className="ri-information-line text-teal-600 mr-2"></i>
                  Auskunftsrecht (Art. 15 DSGVO)
                </h4>
                <p className="text-sm text-gray-700">
                  Sie können Auskunft über Ihre gespeicherten Daten verlangen.
                </p>
              </div>

              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">
                  <i className="ri-edit-line text-teal-600 mr-2"></i>
                  Berichtigungsrecht (Art. 16 DSGVO)
                </h4>
                <p className="text-sm text-gray-700">
                  Sie können die Berichtigung unrichtiger Daten verlangen.
                </p>
              </div>

              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">
                  <i className="ri-delete-bin-line text-teal-600 mr-2"></i>
                  Löschungsrecht (Art. 17 DSGVO)
                </h4>
                <p className="text-sm text-gray-700">
                  Sie können die Löschung Ihrer Daten verlangen, sofern keine gesetzlichen Aufbewahrungspflichten bestehen.
                </p>
              </div>

              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">
                  <i className="ri-download-line text-teal-600 mr-2"></i>
                  Recht auf Datenübertragbarkeit (Art. 20 DSGVO)
                </h4>
                <p className="text-sm text-gray-700">
                  Sie können Ihre Daten in einem strukturierten Format erhalten.
                </p>
              </div>

              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">
                  <i className="ri-close-circle-line text-teal-600 mr-2"></i>
                  Widerspruchsrecht (Art. 21 DSGVO)
                </h4>
                <p className="text-sm text-gray-700">
                  Sie können der Verarbeitung Ihrer Daten aus berechtigtem Interesse widersprechen.
                </p>
              </div>
            </div>

            <p className="text-gray-700 leading-relaxed mt-6">
              Zur Ausübung Ihrer Rechte kontaktieren Sie uns bitte unter:{' '}
              <a href="mailto:info@future-now.online" className="text-teal-600 hover:underline font-semibold">
                info@future-now.online
              </a>
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Datenlöschung</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Sie können Ihren Account und alle damit verbundenen Daten jederzeit löschen:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Über die Account-Einstellungen in der App</li>
              <li>Per E-Mail an info@future-now.online</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              Nach der Löschung werden alle personenbezogenen Daten innerhalb von 30 Tagen vollständig 
              entfernt, sofern keine gesetzlichen Aufbewahrungspflichten bestehen.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Minderjährige</h2>
            <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-4 mb-4">
              <p className="text-sm text-amber-900 leading-relaxed">
                <strong>Wichtig für Eltern:</strong> Learnsnap richtet sich auch an Schüler unter 16 Jahren. 
                Für die Registrierung von Minderjährigen ist die Einwilligung der Erziehungsberechtigten erforderlich.
              </p>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              Wir verarbeiten Daten von Minderjährigen besonders sorgfältig und nur im erforderlichen Umfang 
              für die Bereitstellung unserer Lerndienste.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Änderungen der Datenschutzerklärung</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Wir behalten uns vor, diese Datenschutzerklärung anzupassen, um sie an geänderte Rechtslage 
              oder Änderungen unserer Dienste anzupassen. Die aktuelle Version ist stets auf unserer Website 
              verfügbar.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Beschwerderecht</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde über die Verarbeitung Ihrer 
              personenbezogenen Daten zu beschweren.
            </p>
            <div className="bg-slate-50 rounded-lg p-6">
              <p className="text-gray-700 mb-2"><strong>Zuständige Aufsichtsbehörde:</strong></p>
              <p className="text-gray-700 mb-2">Bayerisches Landesamt für Datenschutzaufsicht</p>
              <p className="text-gray-700 mb-2">Promenade 18</p>
              <p className="text-gray-700 mb-2">91522 Ansbach</p>
              <p className="text-gray-700">
                Website:{' '}
                <a href="https://www.lda.bayern.de" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">
                  www.lda.bayern.de
                </a>
              </p>
            </div>
          </section>

          {/* Final Summary Box */}
          <div className="bg-teal-50 border-l-4 border-teal-500 rounded-lg p-6 mt-8 not-prose">
            <div className="flex items-start gap-3">
              <i className="ri-shield-check-line text-teal-600 text-2xl flex-shrink-0"></i>
              <div>
                <h3 className="text-lg font-bold text-teal-900 mb-2">Zusammenfassung</h3>
                <p className="text-sm text-teal-800 leading-relaxed">
                  Deine Daten sind bei uns sicher! Wir verwenden sie ausschließlich, um dir die bestmögliche 
                  Lernerfahrung zu bieten. Du hast jederzeit die volle Kontrolle über deine Daten und kannst 
                  sie einsehen, ändern oder löschen. Bei Fragen zum Datenschutz sind wir jederzeit für dich da!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
